/**
 * RYZE FRAGRANCES — Custom JavaScript (V4.0)
 * ============================================================
 *
 * PURPOSE:
 * RYZE brand-specific JavaScript for animations, interactions,
 * and page-specific behaviors. Loads globally via theme.liquid.
 *
 * LOADING:
 * Linked in theme.liquid <body> via:
 * {{ 'ryze-custom.js' | asset_url | script_tag }}
 *
 * RULES:
 * - No Lenis smooth scroll (native scroll-behavior: smooth via CSS)
 * - No ScrollTrigger or timeline-based scroll tracking
 * - No backdrop-filter JS manipulation (CSS-only blur effects)
 * - No infinite loops or recursive polling
 * - No animation libraries: the last gsap call was retired 2026-07-31
 *   (Part8); entrances are CSS animations in ryze-custom.css
 * - All event listeners use { passive: true } for scroll
 * - IntersectionObserver threshold: 0.12 for reveal triggers
 *
 * FUNCTIONS:
 *
 * 1. SCROLL REVEAL VIA IntersectionObserver
 *    Selector: .ryze-reveal (opacity 0 → 1, translateY 30px → 0)
 *    - Observes all elements with .ryze-reveal class
 *    - On viewport enter (threshold 0.12), adds .is-visible class
 *    - CSS handles transition: opacity 0.6s, transform 0.6s
 *    - Unobserves after animation (no re-trigger)
 *    - No delay applied in JS (managed by CSS transition-delay)
 *
 * 2. HERO ENTRANCE — retired from JS (see the note at its old slot);
 *    the CTA fade is the ryze-hero-cta-in CSS animation now
 *
 * 3. MOBILE HAMBURGER MENU TOGGLE (MOVED to ryze-header.js, W2 2026-10-18)
 *    Selector: .ryze-nav__hamburger, .ryze-mobile-menu
 *    Behaviors:
 *    - Click hamburger: toggles .is-open on menu, .is-active on button
 *    - Updates aria-expanded attribute for accessibility
 *    - Prevents body scroll when menu open (overflow: hidden)
 *    - Menu links: auto-close on click, restore scroll
 *    CSS handles visibility transition (opacity 0→1, transform)
 *
 * 4. STICKY NAV ON SCROLL (MOVED to ryze-header.js, W2 2026-10-18)
 *    Selector: .ryze-nav
 *    Behavior:
 *    - Listens to window scroll (passive: true)
 *    - Toggles .scrolled class when scrollY > 60px
 *    - CSS changes nav background opacity (0.85 → 0.95)
 *    - No animation threshold; immediate toggle
 *
 * DEPENDENCIES:
 * - ryze-custom.css (.ryze-reveal, .is-visible, .is-open, .is-active,
 *   .scrolled, transition/transform animations)
 * - ryze-hero.liquid (parallax magnetic effect on CTA button)
 *
 * BROWSER COMPATIBILITY:
 * - IntersectionObserver: IE 11 via polyfill, native in modern browsers
 * - Media query (hover: hover) & (pointer: fine): modern browsers,
 *   gracefully skips on touch devices
 *
 * ============================================================
 */

document.addEventListener('DOMContentLoaded', () => {

  // ── 1. Scroll Reveal via IntersectionObserver ────────────────
  const reveals = document.querySelectorAll('.ryze-reveal');
  if (reveals.length) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    reveals.forEach(el => observer.observe(el));
  }

  // ── 2. Hero CTA Fade-in ─────────────────────────────────────
  // Retired 2026-07-31 (Part8): this was the LAST gsap call in the theme,
  // and it kept the whole 72KB library on the homepage for one 0.6s
  // opacity/translate fade. The same entrance now runs as the pure-CSS
  // `ryze-hero-cta-in` animation in ryze-custom.css (reduced-motion
  // gated), and gsap.min.js is no longer loaded by theme.liquid.

  // ── 3, 3b, 4, 5. Drawer, accordions, sticky nav, search: MOVED ─────
  // W2 2026-10-18: the burger/drawer toggle, the accordion toggles, the
  // .scrolled class and the old inline search bar now live in
  // assets/ryze-header.js (THE HEADSTACK, THE LOCK, THE DRAWER, THE PANEL,
  // THE SEARCH), loaded by sections/ryze-header.liquid. Binding them here
  // too would double-toggle every click.

  // ── 6. Product Page: Override Add to Cart (stay on page, upsell) ──
  //
  // Instead of opening a cart drawer or redirecting to /cart,
  // we intercept the product form submit, call /cart/add.js ourselves,
  // update the header cart badge, and swap the button to "ADDED TO CART".
  // This keeps the customer on the product page for upselling via
  // the "Complete Your Collection" section below.

  (function initRyzeAddToCart() {
    var root = (window.Shopify && window.Shopify.routes) ? window.Shopify.routes.root : '/';

    // SVG check icon (shared between submit handler and persist logic)
    var SVG_CHECK =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:16px;height:16px;margin-right:6px;vertical-align:middle;">' +
        '<polyline points="20 6 9 17 4 12"></polyline>' +
      '</svg>';

    // ── Reset button to "Add to Cart" (always allow upselling) ──
    function resetSubmitButton(form) {
      var submitBtn = form.querySelector('[type="submit"]');
      if (!submitBtn) return;
      var btnSpan = submitBtn.querySelector('span');
      submitBtn.classList.remove('ryze-added-to-cart');
      submitBtn.removeAttribute('aria-disabled');
      submitBtn.removeAttribute('disabled');
      if (btnSpan) btnSpan.textContent = window.variantStrings ? window.variantStrings.addToCart : 'Add to cart';
    }

    function resetAllSubmitButtons() {
      var forms = document.querySelectorAll('product-form form[data-type="add-to-cart-form"]');
      forms.forEach(resetSubmitButton);
    }

    // Wait for product-form custom element to be defined
    function hijackProductForms() {
      var forms = document.querySelectorAll('product-form form[data-type="add-to-cart-form"]');
      if (!forms.length) return;

      forms.forEach(function(form) {
        if (form._ryzeHijacked) return;
        form._ryzeHijacked = true;

        form.addEventListener('submit', function(e) {
          e.preventDefault();
          // THE DOUBLE-ADD GUARD (comment corrected 2026-10-18, CODE-27):
          // preventDefault() does NOT stop Dawn's own submit listener. What
          // stops it is order plus state: this listener is registered with
          // useCapture=true, so it runs first, and it sets aria-disabled on the
          // submit button below; Dawn's onSubmitHandler returns early when it
          // reads aria-disabled="true". stopImmediatePropagation stays out so
          // PubSub subscribers still hear the cart events.

          var submitBtn = form.querySelector('[type="submit"]');
          var btnSpan = submitBtn ? submitBtn.querySelector('span') : null;
          if (!submitBtn || submitBtn.getAttribute('aria-disabled') === 'true') return;

          // Already added state — don't re-submit
          if (submitBtn.classList.contains('ryze-added-to-cart')) return;

          // Show loading
          submitBtn.setAttribute('aria-disabled', 'true');
          submitBtn.classList.add('loading');
          var spinner = form.querySelector('.loading__spinner');
          if (spinner) spinner.classList.remove('hidden');
          var originalText = btnSpan ? btnSpan.textContent : '';

          // Build form data
          var formData = new FormData(form);

          fetch(root + 'cart/add.js', {
            method: 'POST',
            body: formData,
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          })
          .then(function(response) { return response.json(); })
          .then(function(data) {
            if (data.status) {
              // Error (e.g., out of stock)
              if (btnSpan) btnSpan.textContent = data.description || 'Error';
              submitBtn.removeAttribute('aria-disabled');
              setTimeout(function() {
                if (btnSpan) btnSpan.textContent = originalText;
              }, 3000);
              return;
            }

            // Success — update button to "ADDED TO CART" temporarily
            submitBtn.classList.add('ryze-added-to-cart');
            if (btnSpan) btnSpan.innerHTML = SVG_CHECK + 'Added to Cart';

            // Auto-revert to "Add to Cart" after 2s for upselling
            setTimeout(function() {
              resetSubmitButton(form);
            }, 2000);

            // Fetch full cart state, then update badge + publish PubSub
            fetch(root + 'cart.js')
              .then(function(r) { return r.json(); })
              .then(function(cart) {
                // Update header cart badge
                var badges = document.querySelectorAll('.ryze-nav__cart-count');
                badges.forEach(function(b) {
                  b.textContent = cart.item_count;
                  b.style.display = cart.item_count > 0 ? 'flex' : 'none';
                  /* Bump ONLY on a real count change, and NEVER during the first second
                     after boot: navigations write every badge fresh (the marker starts
                     empty on a new page), and those load-time writes must initialise
                     silently. The boot anchor is shared: whichever script writes first
                     plants it, so all writers count from the same moment. */
                  window.__ryzeBadgeBoot = window.__ryzeBadgeBoot || Date.now();
                  var ryzeBadgeMoved = b.dataset.ryzeLastCount !== String(b.textContent);
                  b.dataset.ryzeLastCount = String(b.textContent);
                  if (ryzeBadgeMoved && Date.now() - window.__ryzeBadgeBoot > 1000) {
                    b.classList.remove('ryze-badge-bump');
                    void b.offsetWidth;
                    b.classList.add('ryze-badge-bump');
                  }
                });

                // Publish cartUpdate so cart-drawer, cart-items, free-ship
                // bar, and all other PubSub subscribers stay in sync
                if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                  publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'product-form',
                    productVariantId: formData.get('id'),
                    cartData: cart
                  });
                }
              })
              .catch(function() {
                /* Best effort only: the add already succeeded and was
                   reported. A throttled /cart.js read costs the badge and
                   the publish for a moment — the wishlist fetch shim's own
                   follow-up read repairs both. Never surface it (Part8). */
              });

            // Show liquid glass toast notification
            if (window.RyzeToast) window.RyzeToast.show(data.title || data.product_title || 'Item', 'added');
          })
          .catch(function(err) {
            console.error('RYZE Add to Cart error:', err);
            if (btnSpan) btnSpan.textContent = 'Error — try again';
            setTimeout(function() {
              if (btnSpan) btnSpan.textContent = originalText;
            }, 3000);
          })
          .finally(function() {
            submitBtn.classList.remove('loading');
            if (spinner) spinner.classList.add('hidden');
            if (!submitBtn.classList.contains('ryze-added-to-cart')) {
              submitBtn.removeAttribute('aria-disabled');
            }
          });
        }, true); // useCapture = true to fire BEFORE the custom element handler
      });
    }

    // Run immediately and also observe for dynamically loaded product forms
    hijackProductForms();

    // On ANY variant change, reset button to "Add to Cart" immediately
    // so the user can always add more items (upselling).
    document.addEventListener('change', function(e) {
      if (e.target.closest('variant-selects, variant-radios')) {
        resetAllSubmitButtons();
      }
    });

    // ── Replace "Buy It Now" with a simple checkout button ──
    // Shopify's dynamic checkout button uses iframes/shadow DOM
    // that block click interception. We replace it entirely with
    // a clean button that adds the current product to the cart
    // and redirects to /checkout with the full cart.
    /* MSR-01 (fixes 2026-10-18): Shopify's web pixels (Meta, GA4, TikTok)
       learn of an add by watching cart/add.js, then send their AddToCart a
       beat later from their sandboxes (measured on the form path: Meta about
       1.1 s, TikTok 1.5-3.4 s). Navigating the moment the add answered killed
       all three on this path. Hold the confirmed add on screen this long,
       then go: bounded, and only after the store said yes. */
    var BUY_NOW_PIXEL_WAIT_MS = 0; // parked at 0 for the owner's call; 1500 = the measured wait that lets Meta + TikTok AddToCart leave

    function resetBuyNow(btn) {
      btn.textContent = 'BUY IT NOW';
      btn.style.opacity = '';
      btn.removeAttribute('aria-busy');
    }

    /* CODE-05: the page can come back from checkout out of the back-forward
       cache, frozen in REDIRECTING... Release every buy-now button then. */
    window.addEventListener('pageshow', function(e) {
      if (!e.persisted) return;
      document.querySelectorAll('.ryze-buy-now-btn').forEach(resetBuyNow);
    });

    function replaceBuyItNow() {
      var containers = document.querySelectorAll('.shopify-payment-button');
      containers.forEach(function(container) {
        if (container._ryzeReplaced) return;
        container._ryzeReplaced = true;

        // Create our own Buy It Now button (adds to cart + redirects to checkout)
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ryze-buy-now-btn';
        btn.textContent = 'BUY IT NOW';

        btn.addEventListener('click', function() {
          if (btn.getAttribute('aria-busy') === 'true') return;
          var productForm = container.closest('product-form');
          var guardSubmit = productForm ? productForm.querySelector('form [type="submit"]') : null;
          if (guardSubmit && guardSubmit.hasAttribute('disabled')) return; // FE-03
          var form = productForm ? productForm.querySelector('form') : null;
          var variantInput = form ? form.querySelector('[name=id]') : null;
          var variantId = variantInput ? variantInput.value : null;
          var submitBtn = form ? form.querySelector('[type="submit"]') : null;
          var alreadyAdded = submitBtn && submitBtn.classList.contains('ryze-added-to-cart');

          btn.textContent = 'REDIRECTING...';
          btn.style.opacity = '0.7';
          btn.setAttribute('aria-busy', 'true');

          if (alreadyAdded || !variantId) {
            window.location.href = root + 'checkout';
            return;
          }

          /* CODE-04 (fixes 2026-10-18): the form's own data, so the
             quantity selector counts (Dawn's quantity input joins the form
             through its form= attribute); it was a hard-coded 1. */
          var fd = new FormData(form);
          fd.set('id', variantId);
          var askedQty = parseInt(fd.get('quantity'), 10);
          fd.set('quantity', String(askedQty > 0 ? askedQty : 1));

          /* Checkout only on a CONFIRMED add (Part8 fix). The old chain
             navigated in .finally(), so a 422 (sold out, geo gate) or an
             offline blip sent the customer to checkout with nothing added:
             an empty checkout, or an older cart missing the bottle they
             just asked to buy. The STATUS decides, never the parse. */
          fetch(root + 'cart/add.js', {
            method: 'POST',
            body: fd,
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          })
          .then(function(res) {
            return res.json().catch(function() { return null; }).then(function(body) {
              if (!res.ok) {
                var err = new Error(body && body.description ? body.description : 'Could not add to cart.');
                err.ryzeSafe = true;
                throw err;
              }
            });
          })
          .then(function() {
            return new Promise(function(resolve) {
              setTimeout(resolve, BUY_NOW_PIXEL_WAIT_MS);
            });
          })
          .then(function() {
            window.location.href = root + 'checkout';
          })
          .catch(function(err) {
            resetBuyNow(btn);
            if (window.RyzeToast && window.RyzeToast.show) {
              window.RyzeToast.show(err && err.ryzeSafe && err.message ? err.message : 'Could not add to cart. Please try again.', 'error');
            }
          });
        });

        // Insert BUY IT NOW before the Shopify payment button
        container.parentNode.insertBefore(btn, container);

        /* FE-03 (fixes 2026-10-18): Buy it now follows the add button's
           availability. Dawn sets disabled on the submit when the picked size
           is sold out; the buy-now button stepped aside only on a direct load
           of that size, and stayed live after a switch to it (a tap ended in
           a 422). One observer mirrors the state both ways. */
        var ownForm = container.closest('product-form');
        var ownSubmit = ownForm ? ownForm.querySelector('form [type="submit"]') : null;
        if (ownSubmit) {
          var syncBuyNow = function() {
            var soldOut = ownSubmit.hasAttribute('disabled');
            btn.style.display = soldOut ? 'none' : '';
            container.style.display = soldOut ? 'none' : '';
          };
          syncBuyNow();
          new MutationObserver(syncBuyNow).observe(ownSubmit, { attributes: true, attributeFilter: ['disabled'] });
        }

        // The "or express checkout" divider was removed 2026-07-31 (owner
        // order): the buttons stand clean under each other.

        // Show Shopify's dynamic checkout (Apple Pay / Google Pay / Shop Pay)
        // below our custom BUY IT NOW button (not while the size is sold out)
        if (!ownSubmit || !ownSubmit.hasAttribute('disabled')) container.style.display = '';
        container.classList.add('ryze-dynamic-checkout');
      });
    }

    // Run after Shopify renders the payment button
    setTimeout(replaceBuyItNow, 300);
    setTimeout(replaceBuyItNow, 800);
    setTimeout(replaceBuyItNow, 1500);
  })();

  // ── 7. Cart Page: Fix section rendering for RYZE header ──────
  //
  // Dawn's cart.js getSectionsToRender() includes 'cart-icon-bubble'
  // which expects an element with id="cart-icon-bubble" in the DOM.
  // The RYZE custom header doesn't have this element, so cart.js
  // throws a TypeError → catch block shows "error updating cart".
  //
  // Fix: Override getSectionsToRender() on cart-items to exclude
  // cart-icon-bubble. Our header badge updates via pub/sub already.
  (function fixCartSectionRendering() {
    var cartItems = document.querySelector('cart-items');
    if (!cartItems) return; // Not on cart page

    var originalGetSections = cartItems.getSectionsToRender;
    if (!originalGetSections) return;

    cartItems.getSectionsToRender = function() {
      var sections = [];
      // main-cart-items
      var mainItems = document.getElementById('main-cart-items');
      if (mainItems) {
        sections.push({
          id: 'main-cart-items',
          section: mainItems.dataset.id,
          selector: '.js-contents'
        });
      }
      // cart-live-region-text
      var liveRegion = document.getElementById('cart-live-region-text');
      if (liveRegion) {
        sections.push({
          id: 'cart-live-region-text',
          section: 'cart-live-region-text',
          selector: '.shopify-section'
        });
      }
      // main-cart-footer
      var footer = document.getElementById('main-cart-footer');
      if (footer) {
        sections.push({
          id: 'main-cart-footer',
          section: footer.dataset.id,
          selector: '.js-contents'
        });
      }
      // Skip cart-icon-bubble — RYZE header badge updates via pub/sub
      return sections;
    };
  })();

  // ── 8. Subscribe to cart updates — sync all RYZE badges ──────
  //
  // Dawn's cart.js publishes 'cart-update' after quantity changes.
  // Update both the header badge and the hamburger menu badge.
  (function syncRyzeBadges() {
    if (typeof subscribe !== 'function') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, function(event) {
      // NEVER trust a payload (the house rule): the sticky ATC publishes an
      // empty {} on this theme, and treating that as count 0 flipped the
      // badge N -> 0 -> N on every cart touch, which read as random bounces
      // (owner-caught 2026-07-31). A publish without a numeric item_count is
      // ignored; the wishlist fetch shim's follow-up /cart.js read is the
      // truth that lands moments later.
      if (!event || !event.cartData || typeof event.cartData.item_count !== 'number') return;
      var count = event.cartData.item_count;

      // Header badge
      var headerBadges = document.querySelectorAll('.ryze-nav__cart-count');
      headerBadges.forEach(function(b) {
        b.textContent = count;
        b.style.display = count > 0 ? '' : 'none';
        // Bump animation
        /* Bump ONLY on a real count change, and NEVER during the first second
           after boot: navigations write every badge fresh (the marker starts
           empty on a new page), and those load-time writes must initialise
           silently. The boot anchor is shared: whichever script writes first
           plants it, so all writers count from the same moment. */
        window.__ryzeBadgeBoot = window.__ryzeBadgeBoot || Date.now();
        var ryzeBadgeMoved = b.dataset.ryzeLastCount !== String(b.textContent);
        b.dataset.ryzeLastCount = String(b.textContent);
        if (ryzeBadgeMoved && Date.now() - window.__ryzeBadgeBoot > 1000) {
          b.classList.remove('ryze-badge-bump');
          void b.offsetWidth;
          b.classList.add('ryze-badge-bump');
        }
      });

      // Hamburger menu badge
      var menuBadges = document.querySelectorAll('.ryze-mm__badge');
      menuBadges.forEach(function(b) {
        b.textContent = count;
        b.style.display = count > 0 ? '' : 'none';
      });
    });
  })();

});